<template>
  <section>
    <section class="dashboard-page-heading">
      <div v-if="dashboardView" class="dashboard-page-heading__summary d-none d-md-flex">
        <span v-if="dashboardView.icon" class="dashboard-page-heading__icon"><AppIcon :name="dashboardView.icon" /></span>
        <p v-if="dashboardView.subtitle">{{ dashboardView.subtitle }}</p>
      </div>
      <div class="dashboard-page-heading__actions">
        <span class="dashboard-page-heading__actions-start">
          <TabHeaderActions />
        </span>
        <span class="dashboard-page-heading__actions-end">
          <WidgetVisibilityConfig
            v-if="!isMobile"
            :widgets="widgetsMeta"
            :hidden="hiddenWidgets"
            :disabled="!visibilityLoaded"
            :locked="editingLocked"
            @toggle="onToggleWidget"
            @reset="resetWidgetsVisibility"
            @toggle-lock="setEditingLocked($event)"
          />
          <QuickAccessMenu :items="dashboardActions" @select="openAction" />
        </span>
      </div>
    </section>

    <WidgetMasonry
      v-if="visibilityLoaded && !hasWidgetGroups"
      v-model="widgetsOrdered"
      :disabled="!visibilityLoaded || editingLocked || isMobile"
      @order-end="saveWidgetOrder"
      @resize-end="commitWidgetSize"
    >
      <template v-slot:default="{ item }">
        <Widget
          :config-widget="configWidget"
          :reference="item.reference"
          :has-access="item.hasAccess"
          :allow-hide="!editingLocked"
          :allow-drag="!editingLocked && !isMobile"
          :allow-resize="!editingLocked && !isMobile"
          @hide-widget="hideWidget"
        />
      </template>
    </WidgetMasonry>

    <b-tabs
      v-if="visibilityLoaded && hasWidgetGroups"
      v-model="activeWidgetTab"
      class="dashboard-widget-tabs"
      nav-class="dashboard-widget-tabs__nav"
    >
      <b-tab v-for="tab in widgetTabItems" :key="tab.key" :title="tab.title">
        <WidgetMasonry
          :items="widgetsForGroup(tab.key)"
          :disabled="!visibilityLoaded || editingLocked || isMobile"
          @input="mergeWidgetOrder(tab.key, $event)"
          @order-end="saveWidgetOrder"
          @resize-end="commitWidgetSize"
        >
          <template v-slot:default="{ item }">
            <Widget
              :config-widget="configWidget"
              :reference="item.reference"
              :has-access="item.hasAccess"
              :allow-hide="!editingLocked"
              :allow-drag="!editingLocked && !isMobile"
              :allow-resize="!editingLocked && !isMobile"
              @hide-widget="hideWidget"
            />
          </template>
        </WidgetMasonry>
      </b-tab>
    </b-tabs>
  </section>
</template>

<script>
import Helper from '@/handler/helper'
import Session from '@/handler/session'
import Storage from '@/handler/storageLocal'
import Widget from '@/components/inc/widget/widget'
import WidgetVisibilityConfig from '@/components/inc/widget/WidgetVisibilityConfig'
import WidgetMasonry from '@/components/inc/widget/WidgetMasonry'
import DashboardWidgetsHelper from '@/components/inc/widget/DashboardWidgetsHelper'
import QuickAccessMenu from '@/components/inc/dashboard/QuickAccessMenu'
import ServicesPHP from '@/components/modules/users/php/staff/services'
import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
import { EventBus } from '@/handler/event-bus'

export default {
  mixins: [DashboardWidgetsHelper],
  components: { Widget, WidgetVisibilityConfig, WidgetMasonry, QuickAccessMenu, TabHeaderActions },
  props: {
    moduleId: { type: Number, required: true },
    profileId: { type: Number, required: true },
    actions: { type: Array, default: () => [] },
    includePhpCustom: { type: Boolean, default: false },
    widgetFilter: { type: Function, default: null },
    widgetGroups: { type: Object, default: () => ({}) },
    widgetTabs: { type: Array, default: () => [] },
    tabsStorageKey: { type: String, default: '' },
    tabsLegacyMap: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      access: { module_id: this.moduleId, profile_id: this.profileId, view_reference: 'dashboard', elements: {} },
      configWidget: { module_id: this.moduleId, profile_id: this.profileId, elements: {} },
      configSubView: { module_id: this.moduleId, profile_id: this.profileId, elements: {} },
      phpCustom: [],
      activeWidgetTab: 0,
      metadataRevision: 0,
      isMobile: false,
    }
  },
  computed: {
    dashboardView() {
      return Session.getViewMetadata(
        this.moduleId,
        this.profileId,
        'dashboard',
        this.metadataRevision
      )
    },
    dashboardActions() {
      const actions = this.actions.slice()
      if(this.includePhpCustom && Helper.showAccessPHPCustom()) {
        this.phpCustom.forEach(item => actions.push({
          id: 'php-custom-' + item.id,
          title: item.name,
          description: item.observations || 'Herramienta personalizada del módulo',
          icon: 'fa fa-code',
          tone: 'warning',
          dynamic: true,
          route: 'ViewStaffViewPHPCustom',
          params: { id: item.id },
        }))
      }
      return actions
        .filter(action => action.visible !== false)
        .filter(action => action.dynamic || Helper.showAccessView(this.configSubView, action.reference))
        .map(action => {
          if(action.dynamic) return action
          const view = Session.getViewMetadata(
            this.moduleId,
            this.profileId,
            action.reference,
            this.metadataRevision
          )
          return view ? {
            ...action,
            title: action.title || view.title,
            description: action.description || view.subtitle,
            icon: action.icon || view.icon,
          } : null
        })
        .filter(Boolean)
    },
    hasWidgetGroups() {
      return Object.keys(this.widgetGroups).length > 0
    },
    widgetTabItems() {
      if(this.widgetTabs.length) return this.widgetTabs
      return Object.keys(this.widgetGroups).map(key => ({ key, title: key }))
    },
  },
  created() {
    EventBus.$on('ViewMetadata:updated', this.refreshViewMetadata)
    Helper.hasAccessView(this.access)
    this.initDashboardWidgets()
    if(this.includePhpCustom) {
      ServicesPHP.obtenerPHPByModule(this.moduleId).then(response => { this.phpCustom = response.data })
    }
    if(this.tabsStorageKey) {
      const stored = Storage.getValue(this.tabsStorageKey)
      let storedTab = Number(stored)

      if(!Number.isInteger(storedTab) && Object.keys(this.tabsLegacyMap).length) {
        try {
          const legacyTabs = JSON.parse(stored)
          const legacyKey = Object.keys(this.tabsLegacyMap).find(key => legacyTabs && legacyTabs[key])
          if(legacyKey) storedTab = Number(this.tabsLegacyMap[legacyKey])
        } catch(error) {
          // El valor actual se guarda como un número simple.
        }
      }

      if(Number.isInteger(storedTab) && storedTab >= 0 && storedTab < this.widgetTabItems.length) {
        this.activeWidgetTab = storedTab
      }
    }
  },
  mounted() {
    this.updateMobileState()
    window.addEventListener('resize', this.updateMobileState)
  },
  beforeDestroy() {
    EventBus.$off('ViewMetadata:updated', this.refreshViewMetadata)
    window.removeEventListener('resize', this.updateMobileState)
  },
  watch: {
    activeWidgetTab(value) {
      if(this.tabsStorageKey) Storage.setValue(this.tabsStorageKey, String(value))
    },
  },
  methods: {
    refreshViewMetadata() {
      this.metadataRevision++
    },
    updateMobileState() {
      this.isMobile = window.innerWidth <= 992
    },
    filterWidgetsMeta(widget) {
      return this.widgetFilter ? this.widgetFilter(widget) : true
    },
    openAction(action) {
      this.$router.push({ name: action.route, params: action.params || {} })
    },
    widgetsForGroup(group) {
      const references = this.widgetGroups[group] || []
      return this.widgetsOrdered.filter(item => references.includes(item.reference))
    },
    mergeWidgetOrder(group, order) {
      if(this.isMobile) return
      const references = this.widgetGroups[group] || []
      if(!references.length) return
      const current = this.widgetsOrdered.slice()
      const others = current.filter(item => !references.includes(item.reference))
      const firstIndex = current.findIndex(item => references.includes(item.reference))
      const insertAt = firstIndex === -1 ? others.length : firstIndex
      this.widgetsOrdered = others.slice(0, insertAt).concat(order, others.slice(insertAt))
    },
  },
}
</script>

<style>
.dashboard-page-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 18px;
  padding: 0 2px;
}
.dashboard-page-heading__summary {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
}
.dashboard-page-heading__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  font-size: 0.85rem;
}
.dashboard-page-heading p {
  margin: 0;
  color: var(--app-color-text-secondary);
  font-size: var(--app-text-sm);
  font-weight: var(--app-font-medium);
}
.dashboard-page-heading__actions {
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 3px 5px;
  border: 1px solid var(--app-color-border);
  border-radius: 9px;
  background: var(--app-color-surface);
  box-shadow: var(--app-shadow-sm);
}
.dashboard-page-heading__actions-start,
.dashboard-page-heading__actions-end {
  display: flex;
  align-items: center;
}
.dashboard-page-heading__actions .widget-visibility-config {
  display: flex;
  align-items: center;
}
.dashboard-page-heading__actions .widget-config-link,
.dashboard-page-heading__actions .widget-lock-link,
.dashboard-page-heading__actions .changelog-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  margin-right: 2px !important;
  border-radius: 6px;
}
.dashboard-page-heading__actions .widget-config-link:hover,
.dashboard-page-heading__actions .widget-lock-link:hover,
.dashboard-page-heading__actions .changelog-trigger:hover {
  background: var(--app-color-primary-soft);
}
.dashboard-page-heading__actions-end .quick-access-menu {
  margin-left: 8px;
}
@media (max-width: 575.98px) {
  .dashboard-page-heading { align-items: flex-start; }
}
/* Mobile: sin cabecera de ícono/subtítulo (se movió a la burbuja del tab,
   ver docs/tabs-layout/README.md), la botonera pasa a ocupar todo el ancho
   y se separa en dos puntas — fijar/accesos directos contra el margen
   izquierdo, widgets/novedades contra el derecho — en vez de quedar todo
   agrupado en una sola píldora a la derecha. También se agranda un poco
   para un toque más cómodo. */
@media (max-width: 767.98px) {
  .dashboard-widget-tabs__nav {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    overflow-x: auto;
    overflow-y: hidden;
    margin-bottom: 12px;
    padding: 0 2px 8px;
    border-bottom: 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    white-space: nowrap;
  }
  .dashboard-widget-tabs__nav::-webkit-scrollbar {
    display: none;
  }
  .dashboard-widget-tabs__nav > .nav-item {
    flex: 0 0 auto;
  }
  /* El dashboard vive dentro de .small-tabs. Se agrega el wrapper propio
     para que sus tabs internas no hereden el estado activo de las tabs
     principales de la aplicación. */
  .dashboard-widget-tabs .dashboard-widget-tabs__nav .nav-link {
    padding: 6px 14px;
    border: 1px solid transparent;
    border-radius: 20px;
    color: var(--app-color-text-secondary);
    background: transparent;
    font-weight: var(--app-font-medium);
    transition: background-color 0.15s ease, color 0.15s ease;
  }
  .dashboard-widget-tabs .dashboard-widget-tabs__nav .nav-link:hover:not(.active) {
    color: var(--app-color-primary);
    background: var(--app-color-primary-soft);
  }
  .dashboard-widget-tabs .dashboard-widget-tabs__nav .nav-link.active,
  .dashboard-widget-tabs .dashboard-widget-tabs__nav > .nav-item.active > .nav-link {
    background: var(--app-color-primary) !important;
    border-color: var(--app-color-primary) !important;
    border-top-color: var(--app-color-primary) !important;
    color: var(--app-color-on-primary) !important;
    box-shadow: var(--app-shadow-sm);
  }
  .dashboard-page-heading__actions {
    width: 100%;
    justify-content: space-between;
    min-height: 44px;
    padding: 4px 6px;
    border-radius: 10px;
  }
  .dashboard-page-heading__actions .widget-config-link,
  .dashboard-page-heading__actions .widget-lock-link,
  .dashboard-page-heading__actions .changelog-trigger {
    width: 34px;
    height: 34px;
    font-size: 18px;
  }
  .dashboard-page-heading__actions-end .quick-access-menu {
    margin-left: 6px;
  }
}
</style>
